import React from 'react';
import PropTypes from 'prop-types';

const AvailabilityRowerCard = ({
  rower,
  onDragStart,
  isAssigned = false,
  showAvailabilityStatus = false,
}) => {
  const getAvailabilityIndicator = () => {
    if (!showAvailabilityStatus) return null;

    switch (rower.availability_status) {
      case 'fully_available':
        return {
          icon: '✅',
          text: 'Fully Available',
          className: 'text-green-600 bg-green-50 border-green-200',
        };
      case 'partly_available':
        const availablePeriods = rower.availability_details?.conflicts?.[0]?.available_periods;
        const periodsText = availablePeriods
          ? availablePeriods.map((p) => `${p.start_time}-${p.end_time}`).join(', ')
          : `${rower.availability_details?.available_minutes || 0} min`;

        return {
          icon: '⚠️',
          text: `Partly Available (${periodsText})`,
          className: 'text-amber-600 bg-amber-50 border-amber-200',
        };
      case 'not_available':
        if (rower.availability_details?.declared_unavailable_for_session) {
          return {
            icon: '⊘',
            text: 'Unavailable (entered)',
            className: 'text-rose-700 bg-rose-50 border-rose-200',
          };
        }
        return {
          icon: '❌',
          text: 'Not Available',
          className: 'text-red-600 bg-red-50 border-red-200',
        };
      case 'already_assigned':
        return {
          icon: '🚫',
          text: 'Already Assigned',
          className: 'text-gray-600 bg-gray-50 border-gray-200',
        };
      case 'conflict':
        const conflictSession = rower.availability_details?.conflicts?.[0];
        const conflictText = conflictSession
          ? `Conflict: ${conflictSession.session_name}`
          : 'Conflicting Session';

        return {
          icon: '⚠️',
          text: conflictText,
          className: 'text-yellow-600 bg-yellow-50 border-yellow-200',
        };
      default:
        return null;
    }
  };

  const getSidePreferenceIndicator = () => {
    switch (rower.preferred_side) {
      case 'port':
        return { text: 'Portside', className: 'bg-red-100 text-red-800 px-2 py-0.5 rounded-full' };
      case 'starboard':
        return {
          text: 'Starboard',
          className: 'bg-green-100 text-green-800 px-2 py-0.5 rounded-full',
        };
      case 'none':
        return {
          text: 'No Preference',
          className: 'bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full',
        };
      default:
        return {
          text: 'No Preference',
          className: 'bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full',
        };
    }
  };

  const availabilityInfo = getAvailabilityIndicator();
  const sideInfo = getSidePreferenceIndicator();

  const isDisabled = isAssigned;

  return (
    <div
      draggable={!isDisabled}
      onDragStart={!isDisabled ? (e) => onDragStart(e, rower) : undefined}
      className={`
                cursor-move rounded-lg border p-3 transition-all
                ${
                  isDisabled
                    ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-50'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                }
                ${availabilityInfo ? availabilityInfo.className : 'border-gray-200 bg-white'}
            `}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="font-medium text-gray-900">
            {rower.first_name} {rower.last_name}
          </div>
          {/* Side preference indicator */}
          <span className={`text-xs ${sideInfo.className}`} title={`Prefers ${sideInfo.text} side`}>
            {sideInfo.text}
          </span>
          {/* Note: Users don't have strict side preferences, only crews do */}
        </div>

        {/* Availability status indicator */}
        {availabilityInfo && (
          <div className="flex items-center gap-1">
            <span className="text-sm">{availabilityInfo.icon}</span>
            <span className="text-xs font-medium">{availabilityInfo.text}</span>
          </div>
        )}
      </div>

      {/* Additional availability details for partly available rowers */}
      {showAvailabilityStatus &&
        rower.availability_status === 'partly_available' &&
        rower.availability_details?.conflicts && (
          <div className="mt-2 text-xs text-amber-700">
            {rower.availability_details.conflicts.map((conflict, index) => (
              <div key={index}>
                {conflict.type === 'partial_availability' ? (
                  <div className="text-amber-600">
                    ({conflict.available_minutes} of {conflict.total_minutes} minutes available)
                  </div>
                ) : (
                  <div>
                    Conflict: {conflict.session_name} ({conflict.start_time}-{conflict.end_time})
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      {/* Conflict details for rowers with conflicting sessions */}
      {showAvailabilityStatus &&
        rower.availability_status === 'conflict' &&
        rower.availability_details?.conflicts && (
          <div className="mt-2 text-xs text-yellow-700">
            {rower.availability_details.conflicts.map((conflict, index) => (
              <div key={index} className="mb-1">
                <div className="font-medium">{conflict.session_name}</div>
                <div className="text-yellow-600">
                  {conflict.crew_name && `${conflict.crew_name} • `}
                  {conflict.start_time}-{conflict.end_time}
                  {conflict.location && ` • ${conflict.location}`}
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Assignment details for already assigned rowers */}
      {showAvailabilityStatus &&
        rower.availability_status === 'already_assigned' &&
        rower.already_assigned_to && (
          <div className="mt-2 text-xs text-gray-600">
            Assigned to: {rower.already_assigned_to_name || `Crew ${rower.already_assigned_to}`}
          </div>
        )}
    </div>
  );
};

AvailabilityRowerCard.propTypes = {
  rower: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    preferred_side: PropTypes.string,

    availability_status: PropTypes.oneOf([
      'fully_available',
      'partly_available',
      'not_available',
      'already_assigned',
      'conflict',
    ]),
    availability_details: PropTypes.shape({
      available_minutes: PropTypes.number,
      conflicts: PropTypes.array,
      declared_unavailable_for_session: PropTypes.bool,
    }),
    already_assigned_to: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    already_assigned_to_name: PropTypes.string,
  }).isRequired,
  onDragStart: PropTypes.func.isRequired,
  isAssigned: PropTypes.bool,
  showAvailabilityStatus: PropTypes.bool,
};

export default AvailabilityRowerCard;
